import { useCallback, useEffect, useMemo, useState } from "react";
import { ListChecks, Plus } from "lucide-react";
import {
  TASK_STATUS_DISPLAY_ORDER,
  TASK_STATUS_LABELS,
  type Task,
  type TaskPriority,
  type TaskStatus,
} from "@quovy/types";

import { cn } from "../../utils";
import { Button } from "../../inputs/button";
import { INBOX_LAYOUT_ROW_KEYBOARD_SCOPE, InboxLayout } from "../inbox/inbox-layout";
import { MultiSelect, type MultiSelectItem } from "../../inputs/multi-select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../primitives/tooltip";
import { isKeyboardShortcutScopeTarget } from "../../keyboard-shortcuts/target";
import {
  shortcutKey,
  useKeyboardShortcutRegistration,
  type KeyboardShortcutCommand,
} from "../../keyboard-shortcuts/keyboard-shortcuts";
import {
  CreateTaskDialog,
  type CreateTaskInput,
  type PrimaryResourceRef,
} from "../tasks/create-task-dialog";
import type { TaskIdentitySummary } from "../tasks/task-detail-view";
import type {
  MockConflictSource,
  MockLinkedResource,
  MockMissingData,
  MockSourceQuote,
  MockVerifyExtraction,
} from "./mock-data";
import {
  TaskInboxCanvas,
  TaskInboxCanvasCompletion,
  TaskInboxCanvasEmpty,
  defaultTaskTypeRegistry,
  resolveActions,
  resolveDescriptor,
  type TaskInboxAction,
  type TaskInboxCanvasItem,
  type TaskTypeDescriptor,
  type TaskTypeRegistry,
} from "./task-inbox-canvas";

const statusFilterOptions: { value: TaskStatus; label: string }[] = TASK_STATUS_DISPLAY_ORDER.map(
  (value) => ({ value, label: TASK_STATUS_LABELS[value] }),
);

const matchesFilter = (task: { status: TaskStatus }, selected: TaskStatus[]) => {
  if (selected.length === 0) return true;
  return selected.includes(task.status);
};

export type TaskInboxItem = Pick<Task, "id" | "title" | "description" | "status" | "priority"> & {
  taskType?: string;
  sourceQuote?: MockSourceQuote;
  extraction?: MockVerifyExtraction;
  conflictSources?: MockConflictSource[];
  missingData?: MockMissingData;
  linkedResource?: MockLinkedResource;
};

export interface TaskInboxProps {
  tasks: TaskInboxItem[];
  className?: string;
  /**
   * Resource the inbox belongs to. Forwarded to the "New task" dialog so the
   * task is scoped to this resource.
   */
  primaryResource?: PrimaryResourceRef;
  /**
   * Pluggable taskType registry. Defaults to {@link defaultTaskTypeRegistry}.
   */
  registry?: TaskTypeRegistry;
  /** Optional override for canvas resource label (e.g. "Submission"). */
  resourceLabel?: string;
  /** Forwarded to the create-task dialog. */
  assigneeOptions?: TaskIdentitySummary[];
  /** Forwarded to the create-task dialog. When omitted, creation is a no-op. */
  onCreateTask?: (input: CreateTaskInput) => Promise<void> | void;
}

const priorityRailClass: Record<TaskPriority, string> = {
  urgent: "bg-destructive",
  high: "bg-amber-600 dark:bg-amber-500",
  medium: "bg-violet-500 dark:bg-violet-400",
  low: "bg-border",
};

const priorityDotClass: Record<TaskPriority, string> = {
  urgent: "bg-destructive",
  high: "bg-amber-600 dark:bg-amber-500",
  medium: "bg-violet-500 dark:bg-violet-400",
  low: "bg-muted-foreground/40",
};

export function TaskInbox({
  tasks: initialTasks,
  className,
  primaryResource,
  registry = defaultTaskTypeRegistry,
  resourceLabel,
  assigneeOptions,
  onCreateTask,
}: TaskInboxProps) {
  const [tasks, setTasks] = useState<TaskInboxItem[]>(initialTasks);
  const [filter, setFilter] = useState<TaskStatus[]>(["triage", "backlog", "todo"]);
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    const firstTodo = initialTasks.find((t) => t.status === "todo");
    return (firstTodo ?? initialTasks[0])?.id ?? null;
  });
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  const visibleTasks = useMemo(
    () => tasks.filter((t) => matchesFilter(t, filter)),
    [tasks, filter],
  );

  const completeCount = useMemo(() => tasks.filter((t) => t.status === "done").length, [tasks]);
  const statusCounts = useMemo(() => {
    const counts: Partial<Record<TaskStatus, number>> = {};
    for (const t of tasks) {
      counts[t.status] = (counts[t.status] ?? 0) + 1;
    }
    return counts;
  }, [tasks]);
  const filterItems = useMemo<MultiSelectItem<{ label: string; status: TaskStatus }>[]>(
    () =>
      statusFilterOptions.map((opt) => ({
        id: opt.value,
        searchValue: opt.label,
        data: { label: opt.label, status: opt.value },
      })),
    [],
  );
  const totalCount = tasks.length;
  const remaining = totalCount - completeCount;
  const percentComplete = totalCount === 0 ? 0 : Math.round((completeCount / totalCount) * 100);
  const allDone = completeCount === totalCount && totalCount > 0;

  const selected = visibleTasks.find((t) => t.id === selectedId) ?? null;

  const moveSelection = useCallback(
    (direction: 1 | -1) => {
      if (!visibleTasks.length) return;
      const idx = visibleTasks.findIndex((t) => t.id === selectedId);
      const nextIdx = Math.min(
        Math.max((idx === -1 ? 0 : idx) + direction, 0),
        visibleTasks.length - 1,
      );
      setSelectedId(visibleTasks[nextIdx]!.id);
    },
    [visibleTasks, selectedId],
  );

  const advanceWithinFilter = useCallback(
    (fromId: string, previousList: TaskInboxItem[], nextList: TaskInboxItem[]) => {
      const previousVisible = previousList.filter((t) => matchesFilter(t, filter));
      const nextVisible = nextList.filter((t) => matchesFilter(t, filter));
      const idx = previousVisible.findIndex((t) => t.id === fromId);
      if (idx === -1) return nextVisible[0]?.id ?? null;
      const nextVisibleIds = new Set(nextVisible.map((t) => t.id));
      for (let i = idx + 1; i < previousVisible.length; i += 1) {
        const id = previousVisible[i]!.id;
        if (nextVisibleIds.has(id)) return id;
      }
      for (let i = idx - 1; i >= 0; i -= 1) {
        const id = previousVisible[i]!.id;
        if (nextVisibleIds.has(id)) return id;
      }
      return null;
    },
    [filter],
  );

  const completeTask = useCallback(
    (item: TaskInboxCanvasItem) => {
      if (item.status === "done") return;
      const fromId = item.id;
      setTasks((prev) => {
        const next = prev.map((t) => (t.id === fromId ? { ...t, status: "done" as const } : t));
        const nextId = advanceWithinFilter(fromId, prev, next);
        setSelectedId(nextId);
        return next;
      });
    },
    [advanceWithinFilter],
  );

  const skipTask = useCallback(
    (_item: TaskInboxCanvasItem) => {
      moveSelection(1);
    },
    [moveSelection],
  );

  const flagTask = useCallback(
    (item: TaskInboxCanvasItem) => {
      if (item.status === "in_review" || item.status === "done") return;
      const fromId = item.id;
      setTasks((prev) => {
        const next = prev.map((t) =>
          t.id === fromId ? { ...t, status: "in_review" as const } : t,
        );
        const nextId = advanceWithinFilter(fromId, prev, next);
        setSelectedId(nextId);
        return next;
      });
    },
    [advanceWithinFilter],
  );

  // Bind the registry's known action ids (`complete`, `skip`, `flag`) to our
  // local handlers so the descriptor stays declarative.
  const boundDescriptor = useMemo<TaskTypeDescriptor | null>(() => {
    if (!selected) return null;
    const desc = resolveDescriptor(registry, selected as TaskInboxCanvasItem);
    const handlerById: Record<string, (item: TaskInboxCanvasItem) => void> = {
      complete: completeTask,
      skip: skipTask,
      flag: flagTask,
    };
    const wrap = (action: TaskInboxAction): TaskInboxAction => {
      const override = handlerById[action.id];
      if (!override) return action;
      return { ...action, onRun: override };
    };
    return {
      ...desc,
      actions:
        typeof desc.actions === "function"
          ? (item) =>
              (desc.actions as (i: TaskInboxCanvasItem) => TaskInboxAction[])(item).map(wrap)
          : desc.actions.map(wrap),
    };
  }, [registry, selected, completeTask, skipTask, flagTask]);

  const inboxKeyboardCommands = useMemo<KeyboardShortcutCommand[]>(() => {
    const commands: KeyboardShortcutCommand[] = [
      {
        id: "task-inbox-next-item",
        description: "Move to next task",
        bindings: [shortcutKey("j"), shortcutKey("ArrowDown")],
        action: () => moveSelection(1),
        enabled: visibleTasks.length > 0,
        allowInInteractiveTarget: (event) =>
          isKeyboardShortcutScopeTarget(event.target, INBOX_LAYOUT_ROW_KEYBOARD_SCOPE),
        allowRepeat: true,
      },
      {
        id: "task-inbox-previous-item",
        description: "Move to previous task",
        bindings: [shortcutKey("k"), shortcutKey("ArrowUp")],
        action: () => moveSelection(-1),
        enabled: visibleTasks.length > 0,
        allowInInteractiveTarget: (event) =>
          isKeyboardShortcutScopeTarget(event.target, INBOX_LAYOUT_ROW_KEYBOARD_SCOPE),
        allowRepeat: true,
      },
    ];
    if (!selected || !boundDescriptor) return commands;
    const actions = resolveActions(boundDescriptor, selected as TaskInboxCanvasItem);
    for (const [index, action] of actions.entries()) {
      const bindings = [
        ...(index === 0 ? [shortcutKey("Enter")] : []),
        ...(action.shortcut ? [shortcutKey(action.shortcut)] : []),
      ];
      if (bindings.length === 0) continue;
      commands.push({
        id: `task-inbox-action-${action.id}`,
        description: action.label,
        bindings,
        action: () => void action.onRun(selected as TaskInboxCanvasItem),
        allowInInteractiveTarget: (event) =>
          isKeyboardShortcutScopeTarget(event.target, INBOX_LAYOUT_ROW_KEYBOARD_SCOPE),
      });
    }
    return commands;
  }, [boundDescriptor, moveSelection, selected, visibleTasks.length]);
  useKeyboardShortcutRegistration({
    scope: "page",
    commands: inboxKeyboardCommands,
    active: !createOpen,
  });

  const handleCreateTask = useCallback(
    async (input: CreateTaskInput) => {
      if (onCreateTask) {
        await onCreateTask(input);
      }
      setCreateOpen(false);
    },
    [onCreateTask],
  );

  const header = (
    <div className="flex items-center gap-2 px-3 pt-3 pb-2">
      <div className="flex-1 min-w-0">
        <MultiSelect<{ label: string; status: TaskStatus }>
          items={filterItems}
          value={filter}
          onChange={(ids) => setFilter(ids as TaskStatus[])}
          className="h-9 w-full text-xs font-medium"
          searchPlaceholder="Filter statuses..."
          emptyMessage="No statuses."
          renderItem={(item) => (
            <div className="flex flex-1 items-center justify-between gap-4">
              <span>{item.data.label}</span>
              <span className="tabular-nums text-muted-foreground">
                {statusCounts[item.data.status] ?? 0}
              </span>
            </div>
          )}
          renderTrigger={(selectedItems) => {
            if (selectedItems.length === 0) return <span>All statuses</span>;
            if (selectedItems.length === 1) {
              const only = selectedItems[0]!;
              return (
                <span className="flex items-center gap-2">
                  <span>{only.data.label}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {statusCounts[only.data.status] ?? 0}
                  </span>
                </span>
              );
            }
            return <span>{selectedItems.length} statuses</span>;
          }}
        />
      </div>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setCreateOpen(true)}
              aria-label="New task"
              className="size-9 shrink-0 border border-border bg-background text-muted-foreground hover:border-foreground/30 hover:bg-muted/40 hover:text-foreground focus-visible:ring-ring/50"
            >
              <Plus className="size-4" aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">New task</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between border-t border-border/50 px-4 py-2.5 text-xs text-muted-foreground">
      <span className="tabular-nums">{remaining} items remaining</span>
      <span className="tabular-nums">{percentComplete}%</span>
    </div>
  );

  const renderRow = (task: TaskInboxItem, { selected: active }: { selected: boolean }) => {
    const done = task.status === "done";
    return (
      <>
        <span
          aria-hidden="true"
          className={cn(
            "absolute left-1 top-2 bottom-2 w-[3px] rounded-full",
            priorityRailClass[task.priority],
            active && "top-1.5 bottom-1.5",
          )}
        />
        <ListChecks
          className={cn(
            "mt-0.5 size-4 shrink-0",
            done ? "text-muted-foreground" : "text-foreground",
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-[13px] font-medium leading-5",
                done ? "text-muted-foreground line-through" : "text-foreground",
              )}
            >
              {task.title}
            </span>
            <span
              aria-hidden="true"
              className={cn("size-1.5 shrink-0 rounded-full", priorityDotClass[task.priority])}
            />
          </div>
          {task.description ? (
            <div className="mt-0.5 truncate text-xs text-muted-foreground">{task.description}</div>
          ) : null}
        </div>
      </>
    );
  };

  const renderDetail = (task: TaskInboxItem) => {
    if (allDone) return <TaskInboxCanvasCompletion />;
    if (!boundDescriptor) return <TaskInboxCanvasEmpty />;
    return (
      <TaskInboxCanvas
        key={task.id}
        task={task as TaskInboxCanvasItem}
        index={visibleTasks.findIndex((t) => t.id === task.id) + 1}
        total={visibleTasks.length}
        descriptor={boundDescriptor}
        {...(resourceLabel ? { resourceLabel } : {})}
      />
    );
  };

  return (
    <>
      <InboxLayout<TaskInboxItem>
        {...(className ? { className } : {})}
        items={visibleTasks}
        getId={(task) => task.id}
        selectedId={selectedId}
        onSelect={setSelectedId}
        keyboardNavigation={false}
        listLabel="Tasks"
        header={header}
        footer={footer}
        renderRow={renderRow}
        renderDetail={renderDetail}
        emptyList={
          <li className="px-3 py-8 text-center text-xs text-muted-foreground">
            No tasks match this filter.
          </li>
        }
        emptyDetail={allDone ? <TaskInboxCanvasCompletion /> : <TaskInboxCanvasEmpty />}
      />

      <CreateTaskDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreateTask={handleCreateTask}
        {...(primaryResource ? { primaryResource } : {})}
        {...(assigneeOptions ? { assigneeOptions } : {})}
      />
    </>
  );
}
